import { useEffect, useState, useCallback } from 'react';
import { Xmark } from '@gravity-ui/icons';
import { Button } from '@heroui/react';
import { useViewerStore } from '@stores/viewerStore';
import { listInvitedMembers, inviteMember, removeInvitedMember } from '@services/share';
import type { InvitedMember } from '@app-types/scene';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** 受邀成员管理列表（仅创作者可见）。 */
export function InviteList() {
  const sceneId = useViewerStore((s) => s.sceneId);
  const [members, setMembers] = useState<InvitedMember[]>([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMembers = useCallback(async () => {
    if (!sceneId) return;
    const list = await listInvitedMembers(sceneId);
    setMembers(list);
  }, [sceneId]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  async function handleInvite() {
    if (!sceneId) return;
    if (!EMAIL_RE.test(email)) {
      setError('Invalid email format');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await inviteMember(sceneId, email);
      setEmail('');
      await fetchMembers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invite failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove(memberId: string) {
    if (!sceneId) return;
    await removeInvitedMember(sceneId, memberId);
    setMembers((prev) => prev.filter((m) => m.id !== memberId));
  }

  return (
    <div className="border-t border-white/10 pt-4">
      <p className="mb-2 text-xs font-medium text-white/70">Invite by Email</p>
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          aria-label="Invite by email"
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white placeholder-white/30 outline-none focus:border-white/30"
        />
        <Button
          size="sm"
          isPending={loading}
          onPress={() => void handleInvite()}
          className="rounded-lg bg-white/10 text-xs text-white hover:bg-white/20"
        >
          Invite
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}

      {members.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {members.map((m) => (
            <li key={m.id} className="flex items-center justify-between text-xs text-white/70">
              <span className="truncate">{m.name || m.email}</span>
              <button
                type="button"
                onClick={() => void handleRemove(m.id)}
                className="ml-2 rounded p-0.5 text-white/40 hover:text-white"
                aria-label={`Remove ${m.email}`}
              >
                <Xmark width={12} height={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
